import type { Study } from '../../api/types';
import { api } from '../../api/rpc';
import { useStudyActionStore } from '../../stores/studyActionStore';
import { recordStudyCompletion } from '../../stores/readingTempoStore';

/**
 * All 63 StudyAction enums from the GWT client.
 *
 * Each action has:
 *   - id: matches GWT StudyAction enum name
 *   - label: display text
 *   - icon: MUI icon name (or null)
 *   - group: MenuStudyAction group (for dropdown menus), or null for standalone
 *   - rpcMethod: the JSON-RPC method name if it exists, or null
 *   - handler: async function to execute the action
 */
export interface StudyActionDef {
  id: string;
  label: string;
  group: string | null;
  /** Whether the JSON-RPC backend method exists */
  backendReady: boolean;
  /** Execute the action. Returns true on success. */
  handler: (studies: Study[]) => Promise<boolean>;
}

/** All 63 study actions */
export const STUDY_ACTIONS: StudyActionDef[] = [
  // ── Standalone actions ──
  {
    id: 'VIEWSTUDY',
    label: 'View Study',
    group: null,
    backendReady: true,
    handler: async () => {
      // Viewer launch handled elsewhere
      return true;
    },
  },
  {
    id: 'REFRESH',
    label: 'Refresh',
    group: null,
    backendReady: true,
    handler: async () => true,
  },
  {
    id: 'HOLDSTUDY',
    label: 'Hold',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      const reason = window.prompt('Reason for holding study:');
      if (!reason) return false;
      for (const s of studies) {
        await api.holdStudy(s.objKey, reason);
      }
      return true;
    },
  },
  {
    id: 'UNHOLDSTUDY',
    label: 'Unhold',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.unholdStudy(s.objKey);
      }
      return true;
    },
  },
  {
    id: 'EDITSTUDY',
    label: 'Edit Study',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('editStudy', studies);
      return true;
    },
  },
  {
    id: 'GENERATEACC',
    label: 'Generate Accession #',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.generateAccNumber(s.objKey);
      }
      return true;
    },
  },
  {
    id: 'QC_EDITSTUDY',
    label: 'QC Edit Study',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      // QC edit reuses the same edit-study dialog
      useStudyActionStore.getState().openDialog('editStudy', studies);
      return true;
    },
  },
  {
    id: 'SHARESTUDY',
    label: 'Share Study',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('shareStudy', studies);
      return true;
    },
  },
  {
    id: 'SENDSTUDY',
    label: 'Send Study',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('sendStudy', studies);
      return true;
    },
  },
  {
    id: 'DOWNLOADSTUDY',
    label: 'Download Study',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      // Download = DICOM send to current user
      for (const study of studies) {
        await api.sendStudy(study.objKey);
      }
      return true;
    },
  },
  {
    id: 'DOWNLOADSTUDYHTTP',
    label: 'Download Study HTTP',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        const session = localStorage.getItem('brit_sid') ?? '';
        const siteId = (s.site ?? s.siteId ?? 'root').replace(/^site:/, '');
        window.open(`/PacsObjectService/${encodeURIComponent(siteId)}?requestType=WADO&studyUID=${encodeURIComponent(s.uid)}&contentType=application/zip&session=${encodeURIComponent(session)}`, '_blank');
      }
      return true;
    },
  },
  {
    id: 'DOWNLOADFORCD',
    label: 'Download for CD',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        const session = localStorage.getItem('brit_sid') ?? '';
        const siteId = (s.site ?? s.siteId ?? 'root').replace(/^site:/, '');
        window.open(`/PacsObjectService/${encodeURIComponent(siteId)}?requestType=WADO&studyUID=${encodeURIComponent(s.uid)}&contentType=application/zip&includeViewer=true&session=${encodeURIComponent(session)}`, '_blank');
      }
      return true;
    },
  },
  {
    id: 'EDITPATIENT',
    label: 'Edit Patient',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('editPatient', studies);
      return true;
    },
  },
  {
    id: 'EDITORDER',
    label: 'Edit Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('editOrder', studies);
      return true;
    },
  },
  {
    id: 'ADDORDER',
    label: 'Add Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('addOrder', studies);
      return true;
    },
  },
  {
    id: 'MATCHWITHORDER',
    label: 'Match with Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      // Opens QC viewer for order matching
      const { useUIStore } = await import('../../stores/uiStore');
      const study = studies[0];
      if (!study) return false;
      useUIStore.getState().openDynamicTab({
        id: `qc-${study.uid}`,
        label: `QC - ${study.name || study.acc}`.slice(0, 40),
        type: 'viewer',
        study,
        closeable: true,
      });
      return true;
    },
  },
  {
    id: 'AUTOADDORDER',
    label: 'Auto Add Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('autoAddOrder', studies);
      return true;
    },
  },
  {
    id: 'SCHEDULEORDER',
    label: 'Schedule Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('scheduleOrder', studies);
      return true;
    },
  },
  {
    id: 'RESCHEDULEORDER',
    label: 'Reschedule Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('rescheduleOrder', studies);
      return true;
    },
  },
  {
    id: 'AUTHORDER',
    label: 'Auth Order',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('authOrder', studies);
      return true;
    },
  },
  {
    id: 'PAYMENT',
    label: 'Payment',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('payment', studies);
      return true;
    },
  },
  {
    id: 'PATIENT_ORDER_HISTORY',
    label: 'Patient Order History',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('patientOrderHistory', studies);
      return true;
    },
  },
  {
    id: 'CONSULTATION',
    label: 'Consultation',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      // Consultation opens the viewer (same action as VIEWSTUDY / double-click)
      const { useUIStore } = await import('../../stores/uiStore');
      const study = studies[0];
      if (!study) return false;
      useUIStore.getState().openDynamicTab({
        id: `viewer-${study.uid}`,
        label: `${study.name || 'Study'} - ${study.acc || study.uid}`.slice(0, 40),
        type: 'viewer',
        study,
        closeable: true,
      });
      return true;
    },
  },
  {
    id: 'UPLOADREQUEST',
    label: 'Upload Request',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('uploadRequest', studies);
      return true;
    },
  },
  {
    id: 'SHOWNOTES',
    label: 'Show Notes',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('studyNotes', studies);
      return true;
    },
  },
  {
    id: 'SHOWATTACHMENTS',
    label: 'Attachments',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('attachments', studies);
      return true;
    },
  },
  {
    id: 'MISSEDSLA',
    label: 'Missed SLA',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('missedSla', studies);
      return true;
    },
  },
  {
    id: 'ARRIVE_PATIENT',
    label: 'Arrive Patient',
    group: null,
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('arrivePatient', studies);
      return true;
    },
  },

  // ── FINDINGS group ──
  {
    id: 'MARKURGENT',
    label: 'Urgent',
    group: 'FINDINGS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.markUrgent(s.objKey, true, '');
      }
      return true;
    },
  },
  {
    id: 'MARKIMPORTANT',
    label: 'Important',
    group: 'FINDINGS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('markImportant', studies);
      return true;
    },
  },
  {
    id: 'MARKFOLLOWUP',
    label: 'Follow-up',
    group: 'FINDINGS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('markFollowUp', studies);
      return true;
    },
  },
  {
    id: 'DISCORDANCE',
    label: 'Discordance',
    group: 'FINDINGS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('discordance', studies);
      return true;
    },
  },
  {
    id: 'ACKNOWLEDGE_DISCORDANCE',
    label: 'Acknowledge Discordance',
    group: 'FINDINGS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.discordanceViewed(s.objKey);
      }
      return true;
    },
  },

  // ── SET_STATUS group ──
  {
    id: 'MARKORDERED',
    label: 'Mark Ordered',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) await api.updateStudyStatus(s.objKey, 'ORDERED');
      return true;
    },
  },
  {
    id: 'MARKSCHEDULED',
    label: 'Mark Scheduled',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) await api.updateStudyStatus(s.objKey, 'SCHEDULED');
      return true;
    },
  },
  {
    id: 'MARKINPROGRESS',
    label: 'Mark In Progress',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) await api.updateStudyStatus(s.objKey, 'INPROGRESS');
      return true;
    },
  },
  {
    id: 'MARKUNREAD',
    label: 'Mark Unread',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) await api.updateStudyStatus(s.objKey, 'NEWSTUDY');
      return true;
    },
  },
  {
    id: 'MARKDICTATED',
    label: 'Mark Dictated',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.updateStudyStatus(s.objKey, 'DICTATED');
        recordStudyCompletion(s.acc || s.objKey);
      }
      return true;
    },
  },
  {
    id: 'MARKTRANSCRIBED',
    label: 'Mark Transcribed',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.updateStudyStatus(s.objKey, 'TRANSCRIBED');
        recordStudyCompletion(s.acc || s.objKey);
      }
      return true;
    },
  },
  {
    id: 'MARKREPORTED',
    label: 'Mark Reported',
    group: 'SET_STATUS',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.updateStudyStatus(s.objKey, 'REPORTED');
        recordStudyCompletion(s.acc || s.objKey);
      }
      return true;
    },
  },

  // ── NEW_STUDY_ACTIONS group ──
  {
    id: 'COMPLETESTUDY',
    label: 'Complete Study',
    group: 'NEW_STUDY_ACTIONS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('completeStudy', studies);
      return true;
    },
  },
  {
    id: 'ASSIGNSTUDY',
    label: 'Assign Study',
    group: 'NEW_STUDY_ACTIONS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('assignStudy', studies);
      return true;
    },
  },
  {
    id: 'ASSIGN_TO_ME',
    label: 'Assign to Me',
    group: 'NEW_STUDY_ACTIONS',
    backendReady: true,
    handler: async (studies) => {
      const objKeys = studies.map((s) => s.objKey);
      await api.assignStudy(objKeys, null, true);
      return true;
    },
  },
  {
    id: 'ASSIGN_STUDY_TO_RESIDENT',
    label: 'Assign Study to Resident',
    group: 'NEW_STUDY_ACTIONS',
    backendReady: true,
    handler: async (studies) => {
      // Reuses the assignStudy dialog (same backend)
      useStudyActionStore.getState().openDialog('assignStudy', studies);
      return true;
    },
  },
  {
    id: 'RELEASESTUDY',
    label: 'Release Study',
    group: 'NEW_STUDY_ACTIONS',
    backendReady: true,
    handler: async (studies) => {
      const objKeys = studies.map((s) => s.objKey);
      await api.assignStudy(objKeys, null);
      return true;
    },
  },

  // ── REPORTING_TOOLS group ──
  {
    id: 'CREATEREPORT',
    label: 'Add Report',
    group: 'REPORTING_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('createReport', studies);
      return true;
    },
  },
  {
    id: 'EDITREPORT',
    label: 'Edit Report',
    group: 'REPORTING_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('editReport', studies);
      return true;
    },
  },
  {
    id: 'PEERREVIEW',
    label: 'Peer Review',
    group: 'REPORTING_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('peerReview', studies);
      return true;
    },
  },

  // ── SEND_HL7 group ──
  {
    id: 'SENDREPORT',
    label: 'Send Report',
    group: 'SEND_HL7',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Send Reports via HL7',
        `Send all finalized reports for ${studies.length} study(ies) via HL7?`,
        async () => {
          for (const s of studies) await api.sendReportsViaHL7(s.objKey);
        },
      );
      return true;
    },
  },
  {
    id: 'SENDORDER',
    label: 'Send Order',
    group: 'SEND_HL7',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Send Orders via HL7',
        `Send orders for ${studies.length} study(ies) via HL7?`,
        async () => {
          const objKeys = studies.map((s) => s.objKey);
          await api.sendOrdersViaHL7(objKeys);
        },
      );
      return true;
    },
  },
  {
    id: 'SENDIMGAVAILABILITY',
    label: 'Send Img Availability',
    group: 'SEND_HL7',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Send Image Availability',
        `Send image availability for ${studies.length} study(ies)?`,
        async () => {
          const objKeys = studies.map((s) => s.objKey);
          await api.sendImgAvailability(objKeys);
        },
      );
      return true;
    },
  },

  // ── QC group ──
  {
    id: 'QC_OPEN_EDITOR',
    label: 'QC Editor',
    group: 'QC',
    backendReady: true,
    handler: async () => {
      // QC Editor = viewer launch in QC mode (handled by viewer component)
      return true;
    },
  },
  {
    id: 'QC_MARK_UNMARK',
    label: 'QC Mark/Unmark',
    group: 'QC',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'QC Mark / Unmark',
        `Toggle QC status for ${studies.length} study(ies)?`,
        async () => {
          for (const s of studies) {
            // Toggle: if qcstatus is set, clear it; otherwise mark it
            const current = (s as unknown as Record<string, unknown>).qcstatus;
            const newVal = current ? null : 'marked';
            await api.setStudyAttribute(s.objKey, 'qcstatus', newVal);
          }
        },
      );
      return true;
    },
  },
  {
    id: 'QC_BREAK_SESSION',
    label: 'QC Break Session',
    group: 'QC',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Break QC Session',
        `Release the QC session lock for ${studies.length} study(ies)?`,
        async () => {
          for (const s of studies)
            await api.setStudyAttribute(s.objKey, 'qcSessionId', null);
        },
      );
      return true;
    },
  },

  // ── STORAGE group ──
  {
    id: 'STORAGE_OPTIONS',
    label: 'Storage',
    group: 'STORAGE',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('storageOptions', studies);
      return true;
    },
  },
  {
    id: 'DELETEIMAGES',
    label: 'Delete Images',
    group: 'STORAGE',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Delete Images',
        `Delete ALL DICOM images for ${studies.length} study(ies)? This cannot be undone.`,
        async () => {
          for (const s of studies) await api.deleteImagesForStudy(s.objKey);
        },
      );
      return true;
    },
  },
  {
    id: 'DELETEREPORTS',
    label: 'Delete Reports',
    group: 'STORAGE',
    backendReady: true,
    handler: async (studies) => {
      const store = useStudyActionStore.getState();
      store.openConfirmAction(
        studies,
        'Delete Reports',
        `Delete reports for ${studies.length} study(ies)? This cannot be undone.`,
        async () => {
          for (const s of studies) {
            // Load report objects first, then delete by their specific objKeys
            const result = await api.getReports(s.objKey);
            const reportKeys = (result.records ?? [])
              .map((r) => r.objKey)
              .filter(Boolean);
            if (reportKeys.length > 0) {
              await api.deleteObjects({ stuKey: s.objKey, objKeys: reportKeys });
            }
          }
        },
      );
      return true;
    },
  },
  {
    id: 'DELETESTUDYALL',
    label: 'Delete Study',
    group: 'STORAGE',
    backendReady: true,
    handler: async (studies) => {
      for (const s of studies) {
        await api.deleteStudy(s.objKey);
      }
      return true;
    },
  },

  // ── ADMINISTRATIVE_TOOLS group ──
  {
    id: 'STUDYRECOVERY',
    label: 'Study Recovery',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (_studies) => {
      // restoreStudy requires a transaction key — prompt user
      const txKey = window.prompt('Enter the transaction key to restore:');
      if (!txKey) return false;
      await api.restoreStudy(txKey);
      return true;
    },
  },
  {
    id: 'PATIENTRECOVERY',
    label: 'Patient Recovery',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      // Patient recovery = edit patient demographics (reuses editPatient dialog)
      useStudyActionStore.getState().openDialog('editPatient', studies);
      return true;
    },
  },
  {
    id: 'STUDYLOCATIONS',
    label: 'Study Locations',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('studyLocation', studies);
      return true;
    },
  },
  {
    id: 'PATIENTDISCOVERY',
    label: 'Patient Discovery',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('patientDiscovery', studies);
      return true;
    },
  },
  {
    id: 'CALLCONTACTS',
    label: 'Call Contacts',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('callContacts', studies);
      return true;
    },
  },
  {
    id: 'STUDYLOG',
    label: 'Study Log',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('studyLog', studies);
      return true;
    },
  },
  {
    id: 'DICOMDUMP',
    label: 'DICOM Headers',
    group: 'ADMINISTRATIVE_TOOLS',
    backendReady: true,
    handler: async (studies) => {
      useStudyActionStore.getState().openDialog('dicomDump', studies);
      return true;
    },
  },
];

/**
 * Menu group definitions matching GWT MenuStudyAction enum.
 */
export interface MenuGroupDef {
  id: string;
  label: string;
  actions: StudyActionDef[];
}

export const MENU_GROUPS: MenuGroupDef[] = [
  { id: 'FINDINGS', label: 'Findings', actions: STUDY_ACTIONS.filter((a) => a.group === 'FINDINGS') },
  { id: 'NEW_STUDY_ACTIONS', label: 'Study Actions', actions: STUDY_ACTIONS.filter((a) => a.group === 'NEW_STUDY_ACTIONS') },
  { id: 'SET_STATUS', label: 'Set Status', actions: STUDY_ACTIONS.filter((a) => a.group === 'SET_STATUS') },
  { id: 'REPORTING_TOOLS', label: 'Reporting', actions: STUDY_ACTIONS.filter((a) => a.group === 'REPORTING_TOOLS') },
  { id: 'SEND_HL7', label: 'Send HL7', actions: STUDY_ACTIONS.filter((a) => a.group === 'SEND_HL7') },
  { id: 'QC', label: 'QC', actions: STUDY_ACTIONS.filter((a) => a.group === 'QC') },
  { id: 'STORAGE', label: 'Storage', actions: STUDY_ACTIONS.filter((a) => a.group === 'STORAGE') },
  { id: 'ADMINISTRATIVE_TOOLS', label: 'Admin Tools', actions: STUDY_ACTIONS.filter((a) => a.group === 'ADMINISTRATIVE_TOOLS') },
];

/** Get all standalone actions (not in a menu group) */
export const STANDALONE_ACTIONS = STUDY_ACTIONS.filter((a) => a.group === null);
